import { createBrowserRouter, Navigate } from "react-router-dom";
import { Layout } from "../components/layout";
import { Home, Features, Downloads, Future, Changelog, Docs, KnownIssues, ReleaseNotes } from "../pages";
import { DashboardLayout } from "../components/dashboard/DashboardLayout";
import { AdminLayout } from "../components/admin/AdminLayout";
import { Dashboard, ImportProject, AccountSettings, SelfHostedInstances } from "../pages/dashboard";
import { AuthCallback } from "../pages/dashboard/AuthCallback";
import {
  UserManagement,
  InstanceManagement,
  PlanManagement,
  PlatformSettings,
  UpdateManagement,
} from "../pages/admin";
import { CloudAuthWrapper } from "../components/auth/CloudAuthWrapper";
import { KeycloakAuthGate } from "../components/auth/KeycloakAuthGate";
import { LoginRedirect } from "../pages/LoginRedirect";

/**
 * Root layout that enforces Keycloak auth on all pages (closed alpha).
 * CloudAuthWrapper provides the auth context, KeycloakAuthGate enforces login.
 */
function AuthenticatedRoot() {
  return (
    <CloudAuthWrapper>
      <KeycloakAuthGate>
        <Layout />
      </KeycloakAuthGate>
    </CloudAuthWrapper>
  );
}

/**
 * Dashboard section wrapper — CloudAuthWrapper is already at the root,
 * but DashboardLayout has its own auth checks (defense in depth).
 */
function DashboardRoot() {
  return (
    <CloudAuthWrapper>
      <KeycloakAuthGate>
        <DashboardLayout />
      </KeycloakAuthGate>
    </CloudAuthWrapper>
  );
}

/**
 * Admin section wrapper.
 */
function AdminRoot() {
  return (
    <CloudAuthWrapper>
      <KeycloakAuthGate>
        <AdminLayout />
      </KeycloakAuthGate>
    </CloudAuthWrapper>
  );
}

export const router = createBrowserRouter([
  // Auth callback — must be outside auth gate (handles login return)
  { path: "/auth/callback", element: <AuthCallback /> },

  // Login redirect — triggers Keycloak login then redirects back to the originating subdomain
  {
    path: "/login",
    element: (
      <CloudAuthWrapper>
        <KeycloakAuthGate>
          <LoginRedirect />
        </KeycloakAuthGate>
      </CloudAuthWrapper>
    ),
  },

  // Website routes (all protected by Keycloak in closed alpha)
  {
    path: "/",
    element: <AuthenticatedRoot />,
    children: [
      { index: true, element: <Home /> },
      { path: "features", element: <Features /> },
      { path: "downloads", element: <Downloads /> },
      { path: "future", element: <Future /> },
      { path: "known-issues", element: <KnownIssues /> },
      { path: "changelog", element: <Changelog /> },
      { path: "docs", element: <Docs /> },
      { path: "docs/*", element: <Docs /> },
      { path: "releases/notes", element: <ReleaseNotes /> },
      { path: "releases/notes/:date", element: <ReleaseNotes /> },
    ],
  },

  // Dashboard (requires cloud auth + approved status)
  {
    element: <DashboardRoot />,
    children: [
      { path: "/dashboard", element: <Dashboard /> },
      { path: "/dashboard/create", element: <Navigate to="/dashboard" replace /> },
      { path: "/dashboard/import", element: <ImportProject /> },
      { path: "/dashboard/self-hosted", element: <SelfHostedInstances /> },
      { path: "/dashboard/settings", element: <AccountSettings /> },
    ],
  },

  // Admin (requires cloud auth + admin role)
  {
    element: <AdminRoot />,
    children: [
      { path: "/admin", element: <UserManagement /> },
      { path: "/admin/instances", element: <InstanceManagement /> },
      { path: "/admin/plans", element: <PlanManagement /> },
      { path: "/admin/settings", element: <PlatformSettings /> },
      { path: "/admin/updates", element: <UpdateManagement /> },
    ],
  },
]);
